import React from 'react';
import { Moon, Sun, Bell, Menu } from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

export const Header = ({ title, onMenuClick }) => {
  const { theme, toggleTheme, isDark } = useTheme();
  const { user } = useAuth();

  const getInitials = (nome, cognome) => {
    return `${nome?.charAt(0) || ''}${cognome?.charAt(0) || ''}`.toUpperCase();
  };

  const getRoleLabel = (ruolo) => {
    const roleMap = {
      'admin': 'Amministratore',
      'coordinator': 'Coordinator',
      'pm': 'Project Manager',
      'risorsa': 'Risorsa',
    };
    return roleMap[ruolo] || ruolo;
  };

  return (
    <header 
      className="h-16 glass sticky top-0 z-30 flex items-center justify-between px-6"
      data-testid="header"
    >
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={onMenuClick}
          className="md:hidden"
          data-testid="mobile-menu-btn"
        >
          <Menu className="h-5 w-5" />
        </Button>
        <h1 className="text-xl font-bold tracking-tight">{title}</h1>
      </div>

      <div className="flex items-center gap-3">
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="rounded-full"
          data-testid="theme-toggle"
        >
          {isDark ? (
            <Sun className="h-5 w-5" />
          ) : (
            <Moon className="h-5 w-5" />
          )}
        </Button>

        {/* Notifications */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="rounded-full relative"
              data-testid="notifications-btn"
            >
              <Bell className="h-5 w-5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <div className="p-4 text-center text-sm text-muted-foreground">
              Nessuna notifica
            </div>
          </DropdownMenuContent>
        </DropdownMenu>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className="flex items-center gap-2 px-2"
              data-testid="user-menu-btn"
            >
              <Avatar className="h-8 w-8">
                <AvatarFallback className="bg-slate-900 text-white dark:bg-white dark:text-slate-900 text-xs font-medium">
                  {user ? getInitials(user.nome, user.cognome) : 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium leading-none">
                  {user?.nome} {user?.cognome}
                </p>
                <p className="text-xs text-muted-foreground">
                  {user && getRoleLabel(user.ruolo)}
                </p>
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="p-2">
              <p className="text-sm font-medium">{user?.nome} {user?.cognome}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <DropdownMenuItem>
              <Badge variant="secondary" className="mr-2">
                {user && getRoleLabel(user.ruolo)}
              </Badge>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
